import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Autocomplete,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Close as CloseIcon,
  FilterList as FilterIcon,
  Link as LinkIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import api from '../utils/axios';

interface Event {
  id: string;
  title: string;
  description?: string;
  start: string;
  end: string;
  type: 'meeting' | 'task' | 'reminder';
  status: 'pending' | 'completed' | 'cancelled';
  tenantId?: string;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  claimId?: string;
  clientId?: string;
}

interface Claim {
  id: string;
  file_number: string;
  insuredName: string;
  description?: string;
}

interface Client {
  id: string;
  type: 'INDIVIDUAL' | 'COMPANY';
  firstName?: string;
  lastName?: string;
  companyName?: string;
  email?: string;
}

interface CalendarProps {
  isWidget?: boolean;
}

const Calendar: React.FC<CalendarProps> = ({ isWidget = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [events, setEvents] = useState<Event[]>([]);
  const [claims, setClaims] = useState<Claim[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [view, setView] = useState<'dayGridMonth' | 'timeGridWeek' | 'timeGridDay' | 'listWeek'>('dayGridMonth');
  const [filters, setFilters] = useState({
    type: 'all' as 'all' | Event['type'],
    status: 'all' as 'all' | Event['status']
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });
  
  const [selectedClaim, setSelectedClaim] = useState<Claim | null>(null);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start: dayjs(),
    end: dayjs().add(1, 'hour'),
    type: 'meeting' as Event['type'],
    status: 'pending' as Event['status'],
    claimId: null as string | null,
    clientId: null as string | null
  });

  useEffect(() => {
    fetchEvents();
    fetchClaims();
    fetchClients();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get<{ success: boolean; data: Event[] }>('/calendar');
      
      if (response.data.success) {
        setEvents(response.data.data);
      } else {
        setError('Failed to load events');
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
      setError('Failed to load events. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchClaims = async () => {
    try {
      const response = await api.get<{ success: boolean; data: Claim[] }>('/claims');
      if (response.data.success) {
        setClaims(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch claims:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get<{ success: boolean; data: Client[] }>('/clients');
      if (response.data.success) {
        setClients(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const handleDateSelect = (selectInfo: any) => {
    setSelectedDate(dayjs(selectInfo.start));
    setFormData(prev => ({
      ...prev,
      start: dayjs(selectInfo.start),
      end: dayjs(selectInfo.end)
    }));
    setIsModalOpen(true);
  };

  const handleEventClick = (clickInfo: any) => {
    const event = events.find(e => e.id === clickInfo.event.id);
    if (event) {
      setSelectedEvent(event);
      setIsDetailsModalOpen(true);
    }
  };

  const handleEventDrop = async (dropInfo: any) => {
    const event = events.find(e => e.id === dropInfo.event.id);
    if (!event) return;

    try {
      const updatedEvent = {
        ...event,
        start: dropInfo.event.start.toISOString(),
        end: dropInfo.event.end.toISOString()
      };

      const response = await api.put<{ success: boolean; data: Event }>(`/calendar/${event.id}`, updatedEvent);
      
      if (response.data.success) {
        await fetchEvents();
        setSnackbar({
          open: true,
          message: 'Event moved successfully',
          severity: 'success'
        });
      } else {
        throw new Error('Failed to move event');
      }
    } catch (error) {
      console.error('Failed to move event:', error);
      setSnackbar({
        open: true,
        message: 'Failed to move event. Please try again.',
        severity: 'error'
      });
      // Refresh events to revert the visual change
      await fetchEvents();
    }
  };

  const handleEventResize = async (resizeInfo: any) => {
    const event = events.find(e => e.id === resizeInfo.event.id);
    if (!event) return;

    try {
      const updatedEvent = {
        ...event,
        start: resizeInfo.event.start.toISOString(),
        end: resizeInfo.event.end.toISOString()
      };

      const response = await api.put<{ success: boolean; data: Event }>(`/calendar/${event.id}`, updatedEvent);
      
      if (response.data.success) {
        await fetchEvents();
        setSnackbar({
          open: true,
          message: 'Event resized successfully',
          severity: 'success'
        });
      } else {
        throw new Error('Failed to resize event');
      }
    } catch (error) {
      console.error('Failed to resize event:', error);
      setSnackbar({
        open: true,
        message: 'Failed to resize event. Please try again.',
        severity: 'error'
      });
      // Refresh events to revert the visual change
      await fetchEvents();
    }
  };

  const handleEditEvent = () => {
    if (selectedEvent) {
      setFormData({
        title: selectedEvent.title,
        description: selectedEvent.description || '',
        start: dayjs(selectedEvent.start),
        end: dayjs(selectedEvent.end),
        type: selectedEvent.type,
        status: selectedEvent.status,
        claimId: selectedEvent.claimId || '',
        clientId: selectedEvent.clientId || ''
      });
      setIsDetailsModalOpen(false);
      setIsModalOpen(true);
    }
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;

    try {
      setDeleting(true);
      const response = await api.delete<{ success: boolean }>(`/calendar/${selectedEvent.id}`);
      
      if (response.data.success) {
        await fetchEvents();
        setIsDetailsModalOpen(false);
        setSelectedEvent(null);
        setSnackbar({
          open: true,
          message: 'Event deleted successfully',
          severity: 'success'
        });
      } else {
        throw new Error('Failed to delete event');
      }
    } catch (error) {
      console.error('Failed to delete event:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete event. Please try again.',
        severity: 'error'
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedClaim(null);
    setSelectedClient(null);
    setFormData({
      title: '',
      description: '',
      start: dayjs(),
      end: dayjs().add(1, 'hour'),
      type: 'meeting',
      status: 'pending',
      claimId: null,
      clientId: null
    });
  };

  const validateForm = (): string | null => {
    if (!formData.title.trim()) {
      return 'Title is required';
    }
    if (formData.end.isBefore(formData.start)) {
      return 'End time must be after start time';
    }
    if (formData.end.isSame(formData.start)) {
      return 'End time must be different from start time';
    }
    return null;
  };

  const handleModalSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      setSnackbar({
        open: true,
        message: validationError,
        severity: 'error'
      });
      return;
    }

    try {
      setSubmitting(true);
      
      const eventData = {
        title: formData.title,
        description: formData.description,
        start: formData.start.toISOString(),
        end: formData.end.toISOString(),
        type: formData.type,
        status: formData.status,
        claimId: formData.claimId,
        clientId: formData.clientId
      };

      let response;
      if (selectedEvent) {
        // Update existing event
        response = await api.put<{ success: boolean; data: Event }>(`/calendar/${selectedEvent.id}`, eventData);
      } else {
        // Create new event
        response = await api.post<{ success: boolean; data: Event }>('/calendar', eventData);
      }
      
      if (response.data.success) {
        await fetchEvents();
        handleModalClose();
        setSelectedEvent(null);
        setSnackbar({
          open: true,
          message: selectedEvent ? 'Event updated successfully' : 'Event created successfully',
          severity: 'success'
        });
      } else {
        throw new Error(selectedEvent ? 'Failed to update event' : 'Failed to create event');
      }
    } catch (error) {
      console.error('Failed to save event:', error);
      setSnackbar({
        open: true,
        message: `Failed to ${selectedEvent ? 'update' : 'create'} event. Please try again.`,
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getEventColor = (type: string) => {
    switch (type) {
      case 'meeting':
        return '#1976d2';
      case 'task':
        return '#ed6c02';
      case 'reminder':
        return '#2e7d32';
      default:
        return '#757575';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'pending':
        return 'info';
      default:
        return 'default';
    }
  };

  const getLinkedClaim = (claimId?: string) => {
    if (!claimId) return null;
    return claims.find(claim => claim.id === claimId);
  };

  const getLinkedClient = (clientId?: string) => {
    if (!clientId) return null;
    return clients.find(client => client.id === clientId);
  };

  // Filter events based on selected filters
  const filteredEvents = events.filter(event => {
    if (filters.type !== 'all' && event.type !== filters.type) return false;
    if (filters.status !== 'all' && event.status !== filters.status) return false;
    return true;
  });

  // Transform events for FullCalendar
  const calendarEvents = filteredEvents.map(event => ({
    id: event.id,
    title: event.title,
    start: event.start,
    end: event.end,
    backgroundColor: getEventColor(event.type),
    borderColor: getEventColor(event.type),
    textColor: '#ffffff',
    extendedProps: {
      description: event.description,
      type: event.type,
      status: event.status,
      claimId: event.claimId,
      clientId: event.clientId
    }
  }));

  if (isWidget) {
    return (
      <Box p={2} sx={{ position: 'relative' }}>
        {/* Style override for square day cells */}
        <style>{`
          .fc-daygrid-day-frame {
            aspect-ratio: 1 / 1 !important;
            min-height: 0 !important;
            height: auto !important;
          }
          .fc-daygrid-day {
            padding: 0 !important;
          }
        `}</style>
        <FullCalendar
          plugins={[dayGridPlugin]}
          initialView="dayGridMonth"
          height="auto"
          selectable={true}
          select={handleDateSelect}
          events={calendarEvents}
          eventClick={handleEventClick}
          headerToolbar={false}
          dayMaxEvents={3}
          editable={true}
          droppable={true}
          eventDrop={handleEventDrop}
        />
        
        {/* Event Details Modal */}
        <Dialog 
          open={isDetailsModalOpen} 
          onClose={() => {
            setIsDetailsModalOpen(false);
            setSelectedEvent(null);
          }} 
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle>
            Event Details
            <IconButton
              aria-label="close"
              onClick={() => {
                setIsDetailsModalOpen(false);
                setSelectedEvent(null);
              }}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {selectedEvent && (
              <Box sx={{ pt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {selectedEvent.title}
                </Typography>
                
                {selectedEvent.description && (
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    {selectedEvent.description}
                  </Typography>
                )}
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Start:</Typography>
                    <Typography variant="body2">
                      {dayjs(selectedEvent.start).format('MMM D, YYYY h:mm A')}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">End:</Typography>
                    <Typography variant="body2">
                      {dayjs(selectedEvent.end).format('MMM D, YYYY h:mm A')}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Type:</Typography>
                    <Chip
                      label={selectedEvent.type.charAt(0).toUpperCase() + selectedEvent.type.slice(1)}
                      size="small"
                      sx={{ 
                        backgroundColor: getEventColor(selectedEvent.type),
                        color: '#ffffff'
                      }}
                    />
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Status:</Typography>
                    <Chip
                      label={selectedEvent.status.charAt(0).toUpperCase() + selectedEvent.status.slice(1)}
                      color={getStatusColor(selectedEvent.status)}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                  
                  {/* Show linked claim */}
                  {selectedEvent.claimId && (
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">Linked Claim:</Typography>
                      <Chip
                        icon={<AssignmentIcon />}
                        label={getLinkedClaim(selectedEvent.claimId)?.file_number || 'Unknown'}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                  )}
                  
                  {/* Show linked client */}
                  {selectedEvent.clientId && (
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">Linked Client:</Typography>
                      <Chip
                        icon={<PersonIcon />}
                        label={(() => {
                        const client = getLinkedClient(selectedEvent.clientId);
                        if (!client) return 'Unknown';
                        return client.type === 'COMPANY' 
                          ? client.companyName 
                          : `${client.firstName || ''} ${client.lastName || ''}`.trim() || 'Unnamed Client';
                      })()}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setIsDetailsModalOpen(false);
                setSelectedEvent(null);
              }}
              variant="contained"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box p={3} display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={isMobile ? 1 : 3}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
          <Button 
            variant="outlined" 
            size="small" 
            onClick={fetchEvents}
            sx={{ ml: 2 }}
          >
            Retry
          </Button>
        </Alert>
      )}

      <Grid container spacing={isMobile ? 1 : 3}>
        <Grid xs={12} lg={8}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                <Typography variant="h6">Calendar</Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {!isMobile && (
                    <ToggleButtonGroup
                      value={view}
                      exclusive
                      onChange={(_, newView) => newView && setView(newView)}
                      size="small"
                    >
                      <ToggleButton value="dayGridMonth">Month</ToggleButton>
                      <ToggleButton value="timeGridWeek">Week</ToggleButton>
                      <ToggleButton value="timeGridDay">Day</ToggleButton>
                      <ToggleButton value="listWeek">List</ToggleButton>
                    </ToggleButtonGroup>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setSelectedEvent(null);
                      setSelectedDate(dayjs());
                      setIsModalOpen(true);
                    }}
                    size={isMobile ? "small" : "medium"}
                  >
                    {isMobile ? 'Add' : 'Add Event'}
                  </Button>
                </Box>
              </Box>

              {/* Filters */}
              <Box display="flex" gap={2} mb={2} alignItems="center" flexWrap="wrap">
                <FilterIcon color="action" />
                <FormControl size="small" sx={{ minWidth: isMobile ? 100 : 120 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={filters.type}
                    onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value as any }))}
                    input={<OutlinedInput label="Type" />}
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    <MenuItem value="meeting">Meeting</MenuItem>
                    <MenuItem value="task">Task</MenuItem>
                    <MenuItem value="reminder">Reminder</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: isMobile ? 100 : 120 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value as any }))}
                    input={<OutlinedInput label="Status" />}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              
              <FullCalendar
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                initialView={isMobile ? 'listWeek' : view}
                headerToolbar={false}
                height="auto"
                selectable={true}
                selectMirror={true}
                dayMaxEvents={isMobile ? 2 : true}
                weekends={true}
                events={calendarEvents}
                select={handleDateSelect}
                eventClick={handleEventClick}
                editable={!isMobile}
                droppable={!isMobile}
                eventDrop={handleEventDrop}
                eventResize={handleEventResize}
                eventDisplay="block"
                eventTimeFormat={{
                  hour: 'numeric',
                  minute: '2-digit',
                  meridiem: 'short'
                }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={12} lg={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Upcoming Events</Typography>
              {filteredEvents.length === 0 ? (
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                  No upcoming events
                </Typography>
              ) : (
                <List>
                  {filteredEvents
                    .filter(event => dayjs(event.start).isAfter(dayjs()))
                    .sort((a, b) => dayjs(a.start).diff(dayjs(b.start)))
                    .slice(0, isMobile ? 3 : 5)
                    .map(event => (
                      <ListItem 
                        key={event.id} 
                        divider 
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          setSelectedEvent(event);
                          setIsDetailsModalOpen(true);
                        }}
                      >
                        <ListItemText
                          primary={event.title}
                          secondary={
                            <>
                              <Typography
                                component="span"
                                variant="body2"
                                color="text.secondary"
                                display="block"
                              >
                                {dayjs(event.start).format('MMM D, YYYY h:mm A')}
                              </Typography>
                              <Box mt={1} display="flex" gap={1} flexWrap="wrap">
                                <Chip
                                  label={event.type.charAt(0).toUpperCase() + event.type.slice(1)}
                                  size="small"
                                  sx={{ 
                                    backgroundColor: getEventColor(event.type),
                                    color: '#ffffff'
                                  }}
                                />
                                <Chip
                                  label={event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                                  color={getStatusColor(event.status)}
                                  size="small"
                                  variant="outlined"
                                />
                              </Box>
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add/Edit Event Modal */}
      <Dialog open={isModalOpen} onClose={handleModalClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedEvent ? 'Edit Event' : 'Add Event'}
          <IconButton
            aria-label="close"
            onClick={handleModalClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Title"
              fullWidth
              required
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              multiline
              rows={3}
            />
            <TextField
              label="Start"
              type="datetime-local"
              fullWidth
              required
              value={formData.start.format('YYYY-MM-DDTHH:mm')}
              onChange={(e) => setFormData(prev => ({ ...prev, start: dayjs(e.target.value) }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End"
              type="datetime-local"
              fullWidth
              required
              value={formData.end.format('YYYY-MM-DDTHH:mm')}
              onChange={(e) => setFormData(prev => ({ ...prev, end: dayjs(e.target.value) }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              select
              label="Type"
              fullWidth
              required
              value={formData.type}
              onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as Event['type'] }))}
            >
              <MenuItem value="meeting">Meeting</MenuItem>
              <MenuItem value="task">Task</MenuItem>
              <MenuItem value="reminder">Reminder</MenuItem>
            </TextField>
            <TextField
              select
              label="Status"
              fullWidth
              required
              value={formData.status}
              onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as Event['status'] }))}
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </TextField>
            
                         {/* Claim Selection */}
             <Autocomplete
               options={claims}
               getOptionLabel={(option) => `${option.file_number} - ${option.insuredName}`}
               value={selectedClaim}
               onChange={(event, newValue) => {
                 setSelectedClaim(newValue);
                 setFormData({ ...formData, claimId: newValue?.id || null });
               }}
               renderInput={(params) => (
                 <TextField
                   {...params}
                   label="Link to Claim (Optional)"
                   placeholder="Search claims..."
                 />
               )}
               renderOption={(props, option) => (
                 <Box component="li" {...props}>
                   <Box>
                     <Typography variant="body2" fontWeight="bold">
                       {option.file_number}
                     </Typography>
                     <Typography variant="caption" color="text.secondary">
                       {option.insuredName}
                     </Typography>
                   </Box>
                 </Box>
               )}
             />

                         {/* Client Selection */}
             <Autocomplete
               options={clients}
               getOptionLabel={(option) => {
                 const clientName = option.type === 'COMPANY' 
                   ? option.companyName 
                   : `${option.firstName || ''} ${option.lastName || ''}`.trim();
                 return clientName || 'Unnamed Client';
               }}
               value={selectedClient}
               onChange={(event, newValue) => {
                 setSelectedClient(newValue);
                 setFormData({ ...formData, clientId: newValue?.id || null });
               }}
               renderInput={(params) => (
                 <TextField
                   {...params}
                   label="Link to Client (Optional)"
                   placeholder="Search clients..."
                 />
               )}
               renderOption={(props, option) => {
                 const clientName = option.type === 'COMPANY' 
                   ? option.companyName 
                   : `${option.firstName || ''} ${option.lastName || ''}`.trim();
                 return (
                   <Box component="li" {...props}>
                     <Box>
                       <Typography variant="body2" fontWeight="bold">
                         {clientName || 'Unnamed Client'}
                       </Typography>
                       <Typography variant="caption" color="text.secondary">
                         {option.type} • {option.email || 'No email'}
                       </Typography>
                     </Box>
                   </Box>
                 );
               }}
             />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalClose} disabled={submitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleModalSubmit} 
            variant="contained"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : (selectedEvent ? 'Update Event' : 'Add Event')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Event Details Modal */}
      <Dialog 
        open={isDetailsModalOpen} 
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedEvent(null);
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          Event Details
          <IconButton
            aria-label="close"
            onClick={() => {
              setIsDetailsModalOpen(false);
              setSelectedEvent(null);
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedEvent.title}
              </Typography>
              
              {selectedEvent.description && (
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  {selectedEvent.description}
                </Typography>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Start:</Typography>
                  <Typography variant="body2">
                    {dayjs(selectedEvent.start).format('MMM D, YYYY h:mm A')}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">End:</Typography>
                  <Typography variant="body2">
                    {dayjs(selectedEvent.end).format('MMM D, YYYY h:mm A')}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Type:</Typography>
                  <Chip
                    label={selectedEvent.type.charAt(0).toUpperCase() + selectedEvent.type.slice(1)}
                    size="small"
                    sx={{ 
                      backgroundColor: getEventColor(selectedEvent.type),
                      color: '#ffffff'
                    }}
                  />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Status:</Typography>
                  <Chip
                    label={selectedEvent.status.charAt(0).toUpperCase() + selectedEvent.status.slice(1)}
                    color={getStatusColor(selectedEvent.status)}
                    size="small"
                    variant="outlined"
                  />
                </Box>
                
                {/* Show linked claim */}
                {selectedEvent.claimId && (
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">Linked Claim:</Typography>
                    <Chip
                      icon={<AssignmentIcon />}
                      label={getLinkedClaim(selectedEvent.claimId)?.file_number || 'Unknown'}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                )}
                
                {/* Show linked client */}
                {selectedEvent.clientId && (
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">Linked Client:</Typography>
                    <Chip
                      icon={<PersonIcon />}
                      label={(() => {
                      const client = getLinkedClient(selectedEvent.clientId);
                      if (!client) return 'Unknown';
                      return client.type === 'COMPANY' 
                        ? client.companyName 
                        : `${client.firstName || ''} ${client.lastName || ''}`.trim() || 'Unnamed Client';
                    })()}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDeleteEvent}
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
          <Button 
            onClick={handleEditEvent}
            variant="outlined"
            startIcon={<EditIcon />}
          >
            Edit
          </Button>
          <Button 
            onClick={() => {
              setIsDetailsModalOpen(false);
              setSelectedEvent(null);
            }}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Calendar; 